import { tz } from '@date-fns/tz';
import classNames from 'classnames';
import { differenceInMinutes, format, isSameDay, parseISO } from 'date-fns';

import ActionMenu from 'common/ActivityChip/ActionMenu';
import { getActivityActions } from 'common/ActivityChip/ActionMenu/actionMenuFactory';
import { useActivityMutations } from 'common/ActivityChip/ActionMenu/useActivityMutation';
import { useChipModalOpener } from 'common/ActivityChip/useMakeChipModalOpener';
import { formatTime, getDateFnsLocale } from 'utils/datetime';

import styles from 'common/ActivityChip/styles.module.css';

// Weekday + short-month date used only by the absence chip. Not shared with
// the rest of the app (no other UI needs it), so it lives here rather than
// as a new style in formatDateDisplay.
const ABSENCE_DATE_PATTERN = 'EEE dd MMM';

const formatAbsenceDate = (timestamp, timezone) => {
	if (!timestamp) return '';
	const d = parseISO(timestamp);
	if (Number.isNaN(d.getTime())) return '';
	const options = { locale: getDateFnsLocale() };
	if (timezone) options.in = tz(timezone);
	return format(d, ABSENCE_DATE_PATTERN, options);
};

const formatAbsenceDuration = (fromTimestamp, toTimestamp) => {
	const from = parseISO(fromTimestamp);
	const to = parseISO(toTimestamp);
	const totalMinutes = differenceInMinutes(to, from, { roundingMethod: 'round' });
	if (totalMinutes <= 0) return '0m';

	const days = Math.floor(totalMinutes / (60 * 24));
	const remainderMinutes = totalMinutes - days * 60 * 24;
	const nearWholeDayAbove = remainderMinutes <= 1;
	const nearWholeDayBelow = remainderMinutes >= 60 * 24 - 1;
	if (nearWholeDayAbove) {
		const rounded = days;
		if (rounded >= 1) return `${rounded} ${rounded === 1 ? 'day' : 'days'}`;
	}
	if (nearWholeDayBelow) {
		const rounded = days + 1;
		return `${rounded} ${rounded === 1 ? 'day' : 'days'}`;
	}

	const hours = Math.floor(remainderMinutes / 60);
	const minutes = remainderMinutes - hours * 60;

	const parts = [];
	if (days > 0) parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
	if (hours > 0) parts.push(`${hours}h`);
	if (minutes > 0) parts.push(`${minutes}m`);
	return parts.join(' ');
};

const AbsenceLayout = ({
	config,
	timezone,
	isSelected,
	isStartInTimeWindow = true,
	isEndInTimeWindow = true,
	suppressNativeTitle = false,
}) => {
	const { renderType, header, icon, subline, fromTimestamp, toTimestamp, raw, crewMemberId } =
		config;

	const openModalForChip = useChipModalOpener();
	const { updateAbsence, deleteAbsence, loading } = useActivityMutations();

	const activityActions = getActivityActions(
		{ ...raw, renderType, fromTimestamp, toTimestamp, crewMemberId },
		{
			onUpdateAbsence: updateAbsence,
			onDeleteAbsence: deleteAbsence,
		}
	);

	const chipStyles = classNames(styles.activity, styles[renderType], {
		[styles.selected]: isSelected,
		[styles.openStart]: !isStartInTimeWindow,
		[styles.openEnd]: !isEndInTimeWindow,
	});

	const onChipClick = () => {
		const payload = { ...raw, fromTimestamp, toTimestamp };
		if (crewMemberId) payload.crewMemberId = crewMemberId;

		openModalForChip(renderType, payload);
	};

	const resolvedTimezone = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
	const sameDay = isSameDay(parseISO(fromTimestamp), parseISO(toTimestamp), {
		in: tz(resolvedTimezone),
	});

	const fromText = `${formatAbsenceDate(fromTimestamp, resolvedTimezone)} ${formatTime(fromTimestamp, { timezone: resolvedTimezone })}`;
	const toText = sameDay
		? formatTime(toTimestamp, { timezone: resolvedTimezone })
		: `${formatAbsenceDate(toTimestamp, resolvedTimezone)} ${formatTime(toTimestamp, { timezone: resolvedTimezone })}`;
	const rangeText = `${fromText} - ${toText}`;

	return (
		<div
			title={suppressNativeTitle ? undefined : header}
			className={chipStyles}
			onClick={onChipClick}
		>
			<ActionMenu items={activityActions} isLoading={loading} />

			<div className={styles.header}>
				<div>{icon}</div>
				<div className={styles.headerText}>{header}</div>
			</div>

			<div className={styles.subline}>{subline}</div>

			<div className={styles.absenceRange} data-testid="absence-range">
				{rangeText}
			</div>

			<div className={styles.absenceDuration}>
				{formatAbsenceDuration(fromTimestamp, toTimestamp)}
			</div>
		</div>
	);
};

export default AbsenceLayout;
